import type { ReactNode } from 'react';

import { LIST_TABLE_ROW_HEIGHT_CLASS } from '@/components/shared/ListPagination';
import { TABLE_DIVIDER_BORDER } from '@/components/ui/responsive-table/columnLayout';
import { cn } from '@/lib/utils';

interface ListTablePanelProps {
  children: ReactNode;
  /** Paginazione o altro footer — mostrato solo se `showFooter` è true */
  footer?: ReactNode;
  showFooter?: boolean;
  /** Gmail-style: scroll rows only; keep header sticky inside this panel */
  scrollableBody?: boolean;
  /** Stretch panel to fill viewport; footer pinned to panel bottom */
  fillViewport?: boolean;
  className?: string;
}

/** Bordo bianco attorno alla tabella; la tabella è flush (zero padding) sul bordo superiore/laterale. */
export function ListTablePanel({
  children,
  footer,
  showFooter = true,
  scrollableBody = false,
  fillViewport = false,
  className,
}: ListTablePanelProps) {
  const hasFooter = showFooter && footer != null;
  const useFlexLayout = scrollableBody && fillViewport;

  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-card overflow-hidden',
        useFlexLayout && 'flex flex-col flex-1 min-h-0 h-full',
        className,
      )}
    >
      {scrollableBody ? (
        useFlexLayout ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {children}
          </div>
        ) : (
          <div className="overflow-auto max-h-[min(32rem,calc(100vh-13rem))] overscroll-contain">
            {children}
          </div>
        )
      ) : (
        children
      )}
      {hasFooter && (
        <div
          className={cn(
            'shrink-0 flex items-center border-t bg-card px-3',
            TABLE_DIVIDER_BORDER,
            LIST_TABLE_ROW_HEIGHT_CLASS,
            useFlexLayout && 'mt-auto',
          )}
        >
          {footer}
        </div>
      )}
    </div>
  );
}
